/**
 * Main Google Pay button component
 * Refactored into modular structure for better maintainability
 */

import { GOOGLE_PAY_CONFIG } from './google_pay/config';
import GooglePayStripeService from './google_pay/stripe_service';
import GooglePayPaymentService from './google_pay/payment_service';
import GooglePayEventHandlers from './google_pay/event_handlers';
import GooglePayUtils from './google_pay/utils';

export default {

  data() {
    return {
      google_pay_error: null,
      // service instances
      google_pay_stripe_service: null,
      google_pay_payment_service: null,
      google_pay_event_handlers: null,
    };
  },

  computed: {

    // check if Google Pay is enabled via with_browser_pay parameter
    with_browser_pay() {
      return GooglePayUtils.isBrowserPayEnabled();
    },

    // check if Stripe Google Pay should be enabled
    stripe_google_pay_enabled() {
      return GooglePayUtils.isGooglePayEnabled(this.payment_methods);
    },

    // calculate order amount for Google Pay (same logic as PayPal)
    order_amount() {
      if (!this.google_pay_payment_service) {
        this.google_pay_payment_service = new GooglePayPaymentService();
      }
      return this.google_pay_payment_service.calculateOrderAmount(
        this.form, 
        js_data.product.prices
      );
    },

    // check if Google Pay button is valid to display
    google_pay_button_valid() {
      return GooglePayUtils.isButtonValid(this.form);
    }

  },

  watch: {

    'form.deal'() {
      this.google_pay_error = null;
      this.google_pay_update_amount();
    },

    'form.variant'() {
      this.google_pay_error = null;
    },

    'form.warranty'() {
      this.google_pay_update_amount();
    },

    stripe_google_pay_enabled(value) {
      if (value && !this.google_pay_stripe_service) {
        this.google_pay_init();
      }
    },

    // watch for purchase already exists state changes
    isPurchasAlreadyExists(value) {
      // if purchase status changes to false (element becomes available)
      // and Google Pay is enabled, try to initialize
      if (!value && this.stripe_google_pay_enabled && !this.google_pay_stripe_service) {
        this.$nextTick(() => {
          this.google_pay_init();
        });
      }
      
      // if purchase status changes to true (element becomes hidden)
      // cleanup Google Pay to prevent errors
      if (value && this.google_pay_stripe_service) {
        this.google_pay_cleanup();
      }
    }

  },

  mounted() {
    if (this.stripe_google_pay_enabled) {
      // wait for Stripe to be loaded before initializing
      this.google_pay_wait_for_stripe();
    }
  },

  beforeDestroy() {
    // cleanup services when component is destroyed
    this.google_pay_cleanup();
  },

  methods: {

    /**
     * Wait for Stripe library to be available and initialize
     */
    async google_pay_wait_for_stripe() {
      try {
        if (!this.google_pay_stripe_service) {
          this.google_pay_stripe_service = new GooglePayStripeService();
        }
        
        await this.google_pay_stripe_service.waitForStripe();
        this.google_pay_init();
      } catch (error) {
        console.error('Error waiting for Stripe:', error);
        this.google_pay_error = this.t('checkout.payment_error');
      }
    },

    /**
     * Initialize Google Pay integration
     */
    async google_pay_init() {
      try {
        // check if the target element is available before proceeding
        const elementExists = await GooglePayUtils.waitForElement('#google-pay-button', 2000);
        if (!elementExists) {
          console.log('Google Pay button element not available, skipping initialization');
          return;
        }

        // initialize services if not already done
        if (!this.google_pay_stripe_service) {
          this.google_pay_stripe_service = new GooglePayStripeService();
        }
        
        if (!this.google_pay_payment_service) {
          this.google_pay_payment_service = new GooglePayPaymentService();
        }

        // load Stripe key
        const keyResponse = await this.google_pay_stripe_service.getStripeKey();
        if (!keyResponse || !keyResponse.key) {
          console.error('Failed to get Stripe key');
          return;
        }

        // initialize Stripe
        if (!this.google_pay_stripe_service.initializeStripe(keyResponse.key)) {
          return;
        }
        
        // create elements
        if (!this.google_pay_stripe_service.createElements(
          this.order_amount,
          GooglePayUtils.getCurrency()
        )) {
          return;
        }

        // build options with translations
        const options = GooglePayUtils.buildExpressCheckoutOptions(
          this.t.bind(this),
          GOOGLE_PAY_CONFIG
        );

        // create Express Checkout Element (for Google Pay)
        const buttonElement = this.google_pay_stripe_service.createExpressCheckoutElement(options);
        if (!buttonElement) {
          return;
        }

        // mount the button - this will now check element existence
        if (!this.google_pay_stripe_service.mountButton('#google-pay-button')) {
          console.log('Google Pay button mounting failed - element not available');
          return;
        }

        // initialize event handlers
        this.google_pay_event_handlers = new GooglePayEventHandlers(
          this,
          this.google_pay_payment_service,
          this.google_pay_stripe_service
        );

        // bind all event handlers
        this.google_pay_event_handlers.bindEvents();

      } catch (error) {
        console.error('Google Pay initialization error:', error);
        this.google_pay_error = GooglePayUtils.createErrorMessage(error, this.t.bind(this));
      }
    },

    /**
     * Update payment amount when form changes
     */
    google_pay_update_amount() {
      if (this.google_pay_stripe_service && this.order_amount) {
        this.google_pay_stripe_service.updateAmount(this.order_amount);
      }
    },

    /**
     * Cleanup services and prevent memory leaks
     */
    google_pay_cleanup() {
      if (this.google_pay_stripe_service) {
        this.google_pay_stripe_service.destroy();
        this.google_pay_stripe_service = null;
      }
      
      this.google_pay_payment_service = null;
      this.google_pay_event_handlers = null;
    }

  },

};